import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { 
  Package, Truck, CheckCircle, XCircle, Clock, 
  Eye, TrendingUp, DollarSign, RefreshCw, Search 
} from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCustomizeOrders = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  
  const [orders, setOrders] = useState([]);
  const [artworkOrders, setArtworkOrders] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showShipDialog, setShowShipDialog] = useState(false);
  
  // Ship form
  const [shipForm, setShipForm] = useState({
    tracking_company: '',
    tracking_number: '',
    admin_notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const safe = (p) => p.catch(() => ({ data: [] }));
      const [ordersRes, investmentsRes, artworkRes] = await Promise.all([
        safe(axios.get(`${API}/customize/admin/orders`, { headers })),
        safe(axios.get(`${API}/customize/admin/investments`, { headers })),
        safe(axios.get(`${API}/artwork-purchase/admin/orders`, { headers }))
      ]);
      setOrders(ordersRes.data || []);
      setInvestments(investmentsRes.data || []);
      setArtworkOrders(artworkRes.data || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error(t('admin.loadError', 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/customize/admin/orders/${orderId}/status`, { status }, { headers });
      toast.success('Order status updated');
      fetchData();
    } catch (err) {
      toast.error('Update failed: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  const shipOrder = async () => {
    if (!selectedOrder) return;
    if (!shipForm.tracking_company || !shipForm.tracking_number) {
      toast.error('Please fill in carrier and tracking number');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/customize/admin/orders/${selectedOrder.id}/status`, {
        status: 'shipped',
        ...shipForm
      }, { headers });
      toast.success('Shipped successfully');
      setShowShipDialog(false);
      setShipForm({ tracking_company: '', tracking_number: '', admin_notes: '' });
      fetchData();
    } catch (err) {
      toast.error('Ship failed: ' + (err.response?.data?.detail || 'Unknown error'));
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-500', icon: Clock, label: 'Pending' },
      paid: { color: 'bg-blue-500', icon: DollarSign, label: 'Paid' },
      processing: { color: 'bg-purple-500', icon: Package, label: 'Processing' },
      shipped: { color: 'bg-indigo-500', icon: Truck, label: 'Shipped' },
      completed: { color: 'bg-green-500', icon: CheckCircle, label: 'Completed' },
      cancelled: { color: 'bg-red-500', icon: XCircle, label: 'Cancelled' },
      refunded: { color: 'bg-gray-500', icon: RefreshCw, label: 'Refunded' },
      artist_shipped: { color: 'bg-cyan-500', icon: Truck, label: 'Artist Shipped' },
      platform_received: { color: 'bg-teal-500', icon: Package, label: 'Platform Received' },
      delivered: { color: 'bg-emerald-500', icon: CheckCircle, label: 'Delivered' }
    };
    const { color, icon: Icon, label } = config[status] || { color: 'bg-gray-500', icon: Clock, label: status };
    return (
      <Badge className={`${color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getInvestmentStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-500', label: 'Pending' },
      active: { color: 'bg-green-500', label: 'Active' },
      completed: { color: 'bg-gray-500', label: 'Completed' },
      cancelled: { color: 'bg-red-500', label: 'Cancelled' }
    };
    const { color, label } = config[status] || { color: 'bg-gray-500', label: status };
    return <Badge className={`${color} text-white`}>{label}</Badge>;
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        order.id?.toLowerCase().includes(search) ||
        order.user_name?.toLowerCase().includes(search) ||
        order.artwork_title?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Calculate stats
  const orderStats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    paid: orders.filter(o => o.status === 'paid').length,
    processing: orders.filter(o => o.status === 'processing').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    completed: orders.filter(o => o.status === 'completed').length
  };

  const investmentStats = {
    total: investments.length,
    totalAmount: investments.reduce((sum, inv) => sum + (inv.amount || 0), 0),
    totalRevenue: investments.reduce((sum, inv) => sum + (inv.total_revenue || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="admin-customize-orders">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Order Management</h1>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Custom Orders ({orderStats.total})
          </TabsTrigger>
          <TabsTrigger value="artwork-purchases" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Artwork Purchases ({artworkOrders.length})
          </TabsTrigger>
          <TabsTrigger value="investments" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Investments ({investments.length})
          </TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <Card className="bg-gray-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{orderStats.total}</p>
                <p className="text-sm text-gray-500">Total</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{orderStats.pending}</p>
                <p className="text-sm text-yellow-600">Pending</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{orderStats.paid}</p>
                <p className="text-sm text-blue-600">Paid</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{orderStats.processing}</p>
                <p className="text-sm text-purple-600">Processing</p>
              </CardContent>
            </Card>
            <Card className="bg-indigo-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-indigo-600">{orderStats.shipped}</p>
                <p className="text-sm text-indigo-600">Shipped</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{orderStats.completed}</p>
                <p className="text-sm text-green-600">Completed</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search orders, users, artworks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Orders Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Artwork</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img
                            src={getImageUrl(order.artwork_image)}
                            alt=""
                            className="w-12 h-12 rounded object-cover"
                          />
                          <div className="max-w-[150px]">
                            <p className="font-medium truncate">{order.artwork_title}</p>
                            <p className="text-xs text-gray-400 truncate">{order.id.slice(0, 8)}...</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.product_name}</p>
                          <p className="text-sm text-gray-500">
                            {order.size && `${order.size} · `}×{order.quantity}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.user_name || 'Anonymous'}</p>
                          <p className="text-xs text-gray-400">{order.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-bold text-orange-500">{order.total_amount} kWh</span>
                      </TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderDialog(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {order.status === 'paid' && (
                            <Button 
                              size="sm"
                              className="bg-purple-500 hover:bg-purple-600"
                              onClick={() => updateOrderStatus(order.id, 'processing')}
                            >
                              Start Production
                            </Button>
                          )}
                          {order.status === 'processing' && (
                            <Button 
                              size="sm"
                              className="bg-indigo-500 hover:bg-indigo-600"
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowShipDialog(true);
                              }}
                            >
                              <Truck className="w-4 h-4 mr-1" />
                              Ship
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredOrders.length === 0 && (
                <div className="text-center py-12 text-gray-500">No orders yet</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Investments Tab */}
        <TabsContent value="investments">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="bg-orange-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{investmentStats.total}</p>
                <p className="text-sm text-orange-600">Total Investments</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{investmentStats.totalAmount.toLocaleString()}</p>
                <p className="text-sm text-green-600">Total Amount (kWh)</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{investmentStats.totalRevenue.toFixed(2)}</p>
                <p className="text-sm text-blue-600">Distributed Revenue (kWh)</p>
              </CardContent>
            </Card>
          </div>

          {/* Investments Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Artwork</TableHead>
                    <TableHead>Investor</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Total Revenue</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {investments.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img
                            src={getImageUrl(inv.artwork_image)}
                            alt=""
                            className="w-12 h-12 rounded object-cover"
                          />
                          <p className="font-medium truncate max-w-[150px]">{inv.artwork_title}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{inv.user_name || 'Anonymous'}</p>
                          <p className="text-xs text-gray-400">{inv.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-bold text-orange-500">{inv.amount} kWh</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-bold text-green-500">+{(inv.total_revenue || 0).toFixed(2)} kWh</span>
                      </TableCell>
                      <TableCell>{getInvestmentStatusBadge(inv.status)}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(inv.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {investments.length === 0 && (
                <div className="text-center py-12 text-gray-500">No investment records yet</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Artwork Purchases Tab */}
        <TabsContent value="artwork-purchases">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.artwork', 'Artwork')}</TableHead>
                    <TableHead>{t('admin.buyer', 'Buyer')}</TableHead>
                    <TableHead>{t('admin.price', 'Price')}</TableHead>
                    <TableHead>{t('admin.paymentMethod', 'Payment')}</TableHead>
                    <TableHead>{t('admin.status', 'Status')}</TableHead>
                    <TableHead>{t('admin.date', 'Date')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {artworkOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img src={getImageUrl(order.artwork_image)} alt="" className="w-12 h-12 rounded object-cover" />
                          <span className="font-medium text-sm">{order.artwork_title}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{order.buyer_name || order.buyer_id}</TableCell>
                      <TableCell className="font-medium">${order.price || order.amount}</TableCell>
                      <TableCell><Badge variant="outline">{order.payment_method || 'PayPal'}</Badge></TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {artworkOrders.length === 0 && (
                <div className="text-center py-12 text-gray-500">{t('admin.noArtworkPurchases', 'No artwork purchases yet')}</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Order Detail Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <img
                  src={getImageUrl(selectedOrder.artwork_image)}
                  alt=""
                  className="w-20 h-20 rounded object-cover"
                />
                <div>
                  <p className="font-bold">{selectedOrder.artwork_title}</p>
                  <p className="text-sm text-gray-500">{selectedOrder.product_name}</p>
                  {selectedOrder.size && <p className="text-sm text-gray-400">Size: {selectedOrder.size}</p>}
                  <p className="text-sm">Qty: {selectedOrder.quantity}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Order Status</p>
                  {getStatusBadge(selectedOrder.status)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="font-bold text-orange-500">{selectedOrder.total_amount} kWh</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">User</p>
                  <p>{selectedOrder.user_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-sm">{selectedOrder.user_email}</p>
                </div>
              </div>

              {selectedOrder.tracking_number && (
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <p className="text-sm text-indigo-600 font-medium">Shipping Info</p>
                  <p>Carrier: {selectedOrder.tracking_company}</p>
                  <p>Tracking #: {selectedOrder.tracking_number}</p>
                </div>
              )}

              {selectedOrder.has_review && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">User reviewed</p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500">Order Time</p>
                <p>{new Date(selectedOrder.created_at).toLocaleString()}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ship Dialog */}
      <Dialog open={showShipDialog} onOpenChange={setShowShipDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ship Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Carrier *</Label>
              <Select 
                value={shipForm.tracking_company} 
                onValueChange={(v) => setShipForm({ ...shipForm, tracking_company: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SF Express">SF Express</SelectItem>
                  <SelectItem value="YTO Express">YTO Express</SelectItem>
                  <SelectItem value="ZTO Express">ZTO Express</SelectItem>
                  <SelectItem value="Yunda Express">Yunda Express</SelectItem>
                  <SelectItem value="STO Express">STO Express</SelectItem>
                  <SelectItem value="EMS">EMS</SelectItem>
                  <SelectItem value="JD Logistics">JD Logistics</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="UPS">UPS</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tracking Number *</Label>
              <Input
                value={shipForm.tracking_number}
                onChange={(e) => setShipForm({ ...shipForm, tracking_number: e.target.value })}
                placeholder="Enter tracking number"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={shipForm.admin_notes}
                onChange={(e) => setShipForm({ ...shipForm, admin_notes: e.target.value })}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShipDialog(false)}>Cancel</Button>
            <Button onClick={shipOrder} className="bg-indigo-500 hover:bg-indigo-600">
              <Truck className="w-4 h-4 mr-2" />
              Confirm Ship
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomizeOrders;
